import '../i18n';
import {useTranslation} from 'react-i18next';
import { useQuery } from '@apollo/client';
import { QUERY } from '../gql-operations';
import customAnalytics from '../util/custom-analytics';
import React from 'react';
import { AuthContext } from '../util/context';
import { downloadBlobInBrowser, isTauriEnvironment, saveBlobWithTauri } from '../util/downloadHelpers';
import { resolveDownloadFileName } from '../util/downloadFileName';

function useDownloadFile() {
    const {t: lnT} = useTranslation();

    const { refetch } = useQuery(QUERY.DOWNLOAD_FILE, { skip: true });
    const {
        state: { uploadProgress },
        context: { setUploadProgress }
    } = React.useContext(AuthContext);

    const isTauri = isTauriEnvironment();

    const browserDownload = async (url, filename) => {
        const response = await fetch(url);
        const blob = await response.blob();
        downloadBlobInBrowser(blob, filename);
    };

    const downloadFile = async (fileName, fileSize, fileDisplayName) => {
        const displayName = fileDisplayName || fileName || 'file';
        const progressKey = `download_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const updateProgress = (percentage) => {
            setUploadProgress(prev => {
                const newProgress = {
                    ...(prev || {}),
                    [progressKey]: {
                        state: percentage < 100 ? 'pending' : 'done',
                        percentage,
                        type: 'file',
                        name: displayName,
                        size: fileSize
                    }
                };
                return newProgress;
            });
        };
        
        try {
            updateProgress(20);
            
            let { data, error } = await refetch({
                data: {
                    fileName: fileName
                }
            });

            if (error) {
                throw error;
            }

            if (data) {
                console.log('data is', data);
                switch (data.downloadFile.__typename) {
                    case 'S3File':
                        try {
                            const finalFileName = resolveDownloadFileName(fileName, fileDisplayName);

                            if (isTauri) {
                                updateProgress(50);

                                const response = await fetch(data.downloadFile.url);
                                if (!response.ok) throw new Error('Network response was not ok');

                                const blob = await response.blob();
                                const saved = await saveBlobWithTauri(blob, finalFileName);

                                if (saved) {
                                    updateProgress(80);
                                    updateProgress(100);

                                    // Clear progress after a short delay
                                    setTimeout(() => {
                                        const currentProgress = uploadProgress || {};
                                        const { [progressKey]: removed, ...rest } = currentProgress;
                                        setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
                                    }, 2000);
                                } else {
                                    setUploadProgress(prev => {
                                        const { [progressKey]: removed, ...rest } = prev || {};
                                        return Object.keys(rest).length > 0 ? rest : undefined;
                                    });
                                }
                            } else {
                                // Browser download flow with progress tracking
                                updateProgress(60);

                                const response = await fetch(data.downloadFile.url);
                                if (!response.ok) {
                                    throw new Error(`HTTP error! status: ${response.status}`);
                                }

                                updateProgress(80);
                                await browserDownload(data.downloadFile.url, finalFileName);

                                // Mark as complete
                                updateProgress(100);

                                // Clear progress after a short delay
                                setTimeout(() => {
                                    const currentProgress = uploadProgress || {};
                                    const { [progressKey]: removed, ...rest } = currentProgress;
                                    setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
                                }, 2000);
                            }
                        } catch (err) {
                            console.error('Error downloading file:', err);
                            // Remove failed download from progress
                            setUploadProgress(prev => {
                                const { [progressKey]: removed, ...rest } = prev || {};
                                return Object.keys(rest).length > 0 ? rest : undefined;
                            });
                        }
                        break;
                    case 'FileDoesNotBelongToUserError':
                        // Remove error from progress
                        const currentProgress1 = uploadProgress || {};
                        const { [progressKey]: removed1, ...rest1 } = currentProgress1;
                        setUploadProgress(Object.keys(rest1).length > 0 ? rest1 : undefined);
                        alert(lnT('common:fileDoesNotBelongToThisUser'));
                        break;
                    default:
                        // Remove error from progress
                        const currentProgress2 = uploadProgress || {};
                        const { [progressKey]: removed2, ...rest2 } = currentProgress2;
                        setUploadProgress(Object.keys(rest2).length > 0 ? rest2 : undefined);
                        alert(lnT('common:serverError'));
                        break;
                }
            }
        } catch (error) {
            console.error('Error with download query', error);
            // Remove failed download from progress
            const currentProgress = uploadProgress || {};
            const { [progressKey]: removed, ...rest } = currentProgress;
            setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
        }

        customAnalytics.ui_action({
            'action': "Download File",
            "size": fileSize
        })
    };

    return downloadFile;
}

export default useDownloadFile;

// Without returning the whole result
// function useDownloadFile() {
//     const { refetch } = useQuery(QUERY.DOWNLOAD_FILE, { skip: true });

//     const downloadFile = async fileName => {
//         return refetch({
//             data: {
//                 fileName: fileName
//             }
//         });
//     };

//     return downloadFile;
// }

// export default useDownloadFile;
